import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type { QuizletFilters, NewQuizlet, UpdateQuizlet, Quizlet } from '@/types';

// Query options for a single quizlet
export const quizletQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.quizlets.detail(id),
    queryFn: async () => {
      const response = await api.getQuizlet(id);
      return response.quizlet;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

// Hook to fetch all quizlets with optional filters
export function useQuizlets(filters?: QuizletFilters) {
  return useQuery({
    queryKey: queryKeys.quizlets.list(filters || {}),
    queryFn: async () => {
      const response = await api.getQuizlets(filters);
      return response.quizlets;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single quizlet
export function useQuizlet(id: string) {
  return useQuery(quizletQueryOptions(id));
}

// Hook to save a new quizlet
export function useSaveQuizlet() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (quizlet: NewQuizlet) => api.saveQuizlet(quizlet),
    onSuccess: (response) => {
      // Invalidate quizlet list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.lists(),
      });
      // Navigate to the new quizlet
      navigate({
        to: '/quizlets/$quizletId',
        params: { quizletId: response.quizletId },
      });
    },
  });
}

// Hook to update a quizlet
export function useUpdateQuizlet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuizlet }) =>
      api.updateQuizlet(id, data),
    // Optimistic updates for instant UI feedback
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.quizlets.detail(id),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<Quizlet>(
        queryKeys.quizlets.detail(id)
      );

      // Optimistically update
      if (previous) {
        queryClient.setQueryData<Quizlet>(
          queryKeys.quizlets.detail(id),
          { ...previous, ...data }
        );
      }

      return { previous, id };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.quizlets.detail(context.id),
          context.previous
        );
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate to ensure we have latest data
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.lists(),
      });
    },
  });
}

// Hook to delete a quizlet
export function useDeleteQuizlet() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) => api.deleteQuizlet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.lists(),
      });
      navigate({ to: '/quizlets' });
    },
  });
}

// Hook to fetch daily knowledge questions
export function useDailyKnowledgeQuestions() {
  return useQuery({
    queryKey: queryKeys.dailyKnowledge.questions(),
    queryFn: async () => {
      const response = await api.getDailyKnowledgeQuestions();
      return response;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

// ========================================================================
// Question Management Mutations
// ========================================================================

// Hook to add a question to a quizlet
export function useAddQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quizletId, question }: { quizletId: string; question: any }) =>
      api.addQuestion(quizletId, question),
    onSuccess: (_, { quizletId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.detail(quizletId),
      });
    },
  });
}

// Hook to update a question
export function useUpdateQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quizletId,
      questionId,
      question,
    }: {
      quizletId: string;
      questionId: string;
      question: any;
    }) => api.updateQuestion(quizletId, questionId, question),
    onSuccess: (_, { quizletId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.detail(quizletId),
      });
    },
  });
}

// Hook to delete a question
export function useDeleteQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quizletId, questionId }: { quizletId: string; questionId: string }) =>
      api.deleteQuestion(quizletId, questionId),
    onSuccess: (_, { quizletId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.detail(quizletId),
      });
    },
  });
}

// Hook to reorder questions
export function useReorderQuestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quizletId,
      questions,
    }: {
      quizletId: string;
      questions: Array<{ id: string; order_index: number }>;
    }) => api.reorderQuestions(quizletId, questions),
    // Optimistic update for instant feedback
    onMutate: async ({ quizletId, questions }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.quizlets.detail(quizletId),
      });

      const previous = queryClient.getQueryData<Quizlet>(
        queryKeys.quizlets.detail(quizletId)
      );

      // Optimistically update the question order
      if (previous && previous.questions) {
        const reorderedQuestions = [...previous.questions].sort((a, b) => {
          const aOrder = questions.find((q) => q.id === (a as any).id)?.order_index ?? a.order_index;
          const bOrder = questions.find((q) => q.id === (b as any).id)?.order_index ?? b.order_index;
          return aOrder - bOrder;
        });

        queryClient.setQueryData<Quizlet>(queryKeys.quizlets.detail(quizletId), {
          ...previous,
          questions: reorderedQuestions,
        });
      }

      return { previous, quizletId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.quizlets.detail(context.quizletId),
          context.previous
        );
      }
    },
    onSuccess: (_, { quizletId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quizlets.detail(quizletId),
      });
    },
  });
}
